import type * as Types from '__generated__/zeus/types';

/** Shaped rather than derived: the two callers read these fields off different generated queries. */
type MaybeCnmlTransaction = {
  __typename: string;
  selectedProduct?: Types.SellerProduct | null;
};

export const CNML_HOME_RESALE_DETAILS_FLAG = 'offers-cnml-home-resale-details-09-26';

/** GrowthBook serializes this boolean flag's TRUE as `'true'`, not `'on'`. */
export const isCnmlHomeResaleFlagOn = (variant: string): boolean =>
  variant === 'on' || variant === 'true';

export const isCnmlClosedResale = (transaction: MaybeCnmlTransaction | null | undefined): boolean =>
  transaction?.__typename === 'ClosedTransaction' &&
  transaction.selectedProduct === 'SELL_WITH_UPSIDE';

export const homeResaleDetailsPath = (addressId: string): string =>
  `/dashboard/resale/${addressId}`;

export type ResalePhase = 'PreparingHomeResale' | 'ListedHomeResale' | 'SoldHomeResale';

/** "Sold to Opendoor" has no `HomeResale` type: it is done before the resale starts. */
export const RESALE_STEPS: ReadonlyArray<{ label: string; phase: ResalePhase | null }> = [
  { label: 'Sold to Opendoor', phase: null },
  { label: 'Renovation', phase: 'PreparingHomeResale' },
  { label: 'On the market', phase: 'ListedHomeResale' },
  { label: 'Sold', phase: 'SoldHomeResale' },
];

export const resaleStepIndex = (phase: ResalePhase | null | undefined): number => {
  const found = RESALE_STEPS.findIndex((step) => step.phase === phase);
  // An unknown phase falls back to the acquisition step rather than blanking the tracker.
  return found === -1 ? 0 : found;
};

export const resalePhaseLabel = (phase: ResalePhase | null | undefined): string =>
  RESALE_STEPS[resaleStepIndex(phase)].label;

const dayDisplay = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' });

/**
 * `Date` scalars are zoneless calendar days, so they are rebuilt in local time
 * rather than parsed, which would shift them by the viewer's offset.
 */
export const formatResaleDay = (value: string | null | undefined): string | null => {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  if (!match) {
    return null;
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const parsed = new Date(year, month - 1, day);
  // `Date` rolls impossible days forward: 2026-02-30 would format as March 2.
  return parsed.getMonth() === month - 1 && parsed.getDate() === day
    ? dayDisplay.format(parsed)
    : null;
};
